/* eslint-disable complexity */
import React from 'react';
import { useIntl } from 'react-intl';

import Box from 'carbon-react/lib/components/box';

import { ForecastPreviousActuals, ForecastPreviousActualsProps } from '@futrli/predict/forecast/create-forecast/components/form-elements/forecast-previous-actuals';
import { IconMessage } from '@futrli/predict/forecast/create-forecast/components/form-elements/icon-message/icon-message';
import { WizardRequiredFieldInfo } from '@futrli/ui-components/components/sidebar/wizard/wizard-required-field-info/wizard-required-field-info';

import { ForecastDuration, ForecastDurationProps } from '../../../components/form-elements/forecast-duration';
import { ForecastFormErrors } from '../../../components/form-elements/types';
import { ForecastFrom, ForecastFromProps } from '../../../components/form-elements/forecast-from';
import { ForecastName, ForecastNameProps } from '../../../components/form-elements/forecast-name';
import { UpdateForecastFormType } from '../types';

import styles from '../../../components/form-elements/elements.scss';

interface UpdateForecastFormProps {
  duration: string;
  errors?: ForecastFormErrors;
  formType: UpdateForecastFormType;
  from: string;
  maxDate?: string;
  minDate?: string;
  name: string;
  onChangeDuration: ForecastDurationProps['onChange'];
  onChangeFrom: ForecastFromProps['onChange'];
  onChangeName: ForecastNameProps['onChange'];
  onChangePreviousActuals: ForecastPreviousActualsProps['onChange'];
  previousActuals: boolean;
  revalidate: () => void;
}

export const UpdateForecastForm = ({
  errors = {},
  duration,
  formType,
  from,
  maxDate,
  minDate,
  name,
  onChangeDuration,
  onChangeFrom,
  onChangeName,
  onChangePreviousActuals,
  previousActuals,
  revalidate
}: UpdateForecastFormProps) => {
  const intl = useIntl();
  const isBudget = formType === 'budget';
  const isFixedForecast = formType === 'fixed';
  const isRollingForecast = formType === 'rolling';

  return (
    <Box className={styles.carbonFormFixes}>
      <WizardRequiredFieldInfo />
      <ForecastName
        value={name}
        onChange={onChangeName}
        label={intl.formatMessage(
          isBudget
            ? { id: 'predict.create_forecast.budget_name', defaultMessage: 'Budget name' }
            : { id: 'predict.create_forecast.forecast_name', defaultMessage: 'Forecast name' }
        )}
        error={errors.name?.message}
        onBlur={revalidate}
      />
      {!isRollingForecast && (
        <ForecastFrom
          maxDate={maxDate}
          minDate={minDate}
          value={from}
          onChange={onChangeFrom}
          label={intl.formatMessage({ id: 'common.start_date', defaultMessage: 'Start date' })}
          error={errors.from?.message}
          onBlur={revalidate}
        />
      )}
      {isBudget && (
        <IconMessage mb={3}>
          {intl.formatMessage({
            id: 'predict.create_forecast.duration_is_one_year_from_start_date',
            defaultMessage: 'Duration is 1 year from the start date'
          })}
        </IconMessage>
      )}
      {isFixedForecast && (
        <ForecastDuration
          type="fixedForecast"
          value={duration}
          onChange={onChangeDuration}
          legend={intl.formatMessage({ id: 'predict.create_forecast.duration', defaultMessage: 'Duration' })}
          error={errors.duration?.message}
          onBlur={revalidate}
        />
      )}
      {isRollingForecast && (
        <>
          <IconMessage mb={3}>
            {intl.formatMessage({
              id: 'predict.create_forecast.forecast_data_always_starts_from_the_last_completed_sync',
              defaultMessage: 'Forecast data always starts from the last completed sync'
            })}
          </IconMessage>
          <ForecastDuration
            type="rollingForecast"
            value={duration}
            onChange={onChangeDuration}
            legend={intl.formatMessage({ id: 'predict.create_forecast.duration', defaultMessage: 'Duration' })}
            error={errors.duration?.message}
            onBlur={revalidate}
            mb={4}
          />
          <ForecastPreviousActuals
            checked={previousActuals}
            onChange={onChangePreviousActuals}
            error={errors.previousActuals?.message}
            onBlur={revalidate}
            mb={0}
          />
        </>
      )}
    </Box>
  );
};
